import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Plus, Trash2 } from 'lucide-react';
import DateInput from '@/components/DateInput';

export default function TareaFormModal({
  open,
  onOpenChange,
  tareaForm,
  setTareaForm,
  editingTarea,
  onSave,
  labores,
  lotes,
  trabajadores,
  centrosPorLote,
  onLoteChange,
  nuevaSubtarea,
  setNuevaSubtarea,
  onAgregarSubtarea,
  onEliminarSubtarea,
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{editingTarea ? 'Editar Tarea' : 'Nueva Tarea'}</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-2">
            <Label>Título *</Label>
            <Input
              value={tareaForm.titulo}
              onChange={(e) => setTareaForm({ ...tareaForm, titulo: e.target.value })}
              placeholder="Ej: Aplicar fertilizante en Lote A"
            />
          </div>

          <div className="space-y-2">
            <Label>Descripción</Label>
            <Textarea
              value={tareaForm.descripcion}
              onChange={(e) => setTareaForm({ ...tareaForm, descripcion: e.target.value })}
              placeholder="Detalles..."
              rows={3}
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Prioridad</Label>
              <Select
                value={tareaForm.prioridad}
                onValueChange={(value) => setTareaForm({ ...tareaForm, prioridad: value })}
              >
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="baja">🟢 Baja</SelectItem>
                  <SelectItem value="media">🟡 Media</SelectItem>
                  <SelectItem value="alta">🔴 Alta</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Fecha Planificada</Label>
              <DateInput
                value={tareaForm.fecha_planificada}
                onChange={(value) => setTareaForm({ ...tareaForm, fecha_planificada: value })}
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label>Labor *</Label>
            <Select
              value={tareaForm.labor_id || undefined}
              onValueChange={(value) => {
                const labor = labores.find(l => l.id === value);
                setTareaForm({
                  ...tareaForm,
                  labor_id: value,
                  labor_nombre: labor ? labor.nombre : ''
                });
              }}
            >
              <SelectTrigger><SelectValue placeholder="Seleccione una labor..." /></SelectTrigger>
              <SelectContent>
                <div className="px-2 py-1 text-xs font-semibold text-gray-500">PRODUCCIÓN</div>
                {labores.filter(l => l.tipo === 'produccion').map((labor) => (
                  <SelectItem key={labor.id} value={labor.id}>
                    {labor.nombre}
                  </SelectItem>
                ))}
                <div className="px-2 py-1 text-xs font-semibold text-gray-500 mt-2">MANTENIMIENTO</div>
                {labores.filter(l => l.tipo === 'mantenimiento').map((labor) => (
                  <SelectItem key={labor.id} value={labor.id}>
                    {labor.nombre}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Lote *</Label>
              <Select
                value={tareaForm.lote_id || undefined}
                onValueChange={onLoteChange}
              >
                <SelectTrigger><SelectValue placeholder="Seleccione un lote..." /></SelectTrigger>
                <SelectContent>
                  {lotes.map((lote) => (
                    <SelectItem key={lote.id} value={lote.id}>
                      {lote.nombre}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Centros (opcional)</Label>
              <Select
                value={(tareaForm.centros && tareaForm.centros[0]) || undefined}
                onValueChange={(value) => {
                  setTareaForm({
                    ...tareaForm,
                    centros: value ? [value] : []
                  });
                }}
                disabled={!tareaForm.lote_id}
              >
                <SelectTrigger>
                  <SelectValue placeholder={tareaForm.lote_id ? "Seleccione centros..." : "Primero seleccione un lote"} />
                </SelectTrigger>
                <SelectContent>
                  {centrosPorLote.map((centro) => (
                    <SelectItem key={centro.id} value={centro.id}>
                      Centro {centro.numero_centro}
                    </SelectItem>
                  ))}
                  {centrosPorLote.length === 0 && tareaForm.lote_id && (
                    <div className="px-2 py-1 text-xs text-gray-500">No hay centros en este lote</div>
                  )}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="space-y-2">
            <Label>Responsable</Label>
            <Select
              value={tareaForm.responsable_id || undefined}
              onValueChange={(value) => {
                const trabajador = trabajadores.find(t => t.id === value);
                setTareaForm({
                  ...tareaForm,
                  responsable_id: value,
                  responsable_nombre: trabajador ? trabajador.nombre_completo : ''
                });
              }}
            >
              <SelectTrigger><SelectValue placeholder="Seleccione..." /></SelectTrigger>
              <SelectContent>
                {trabajadores.map((trab) => (
                  <SelectItem key={trab.id} value={trab.id}>
                    {trab.nombre_completo}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label>Subtareas</Label>
            <div className="flex gap-2">
              <Input
                value={nuevaSubtarea}
                onChange={(e) => setNuevaSubtarea(e.target.value)}
                placeholder="Agregar subtarea..."
                onKeyPress={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    onAgregarSubtarea();
                  }
                }}
              />
              <Button type="button" onClick={onAgregarSubtarea}>
                <Plus className="w-4 h-4" />
              </Button>
            </div>
            <div className="space-y-1 mt-2">
              {tareaForm.subtareas.map((st, index) => (
                <div key={index} className="flex items-center justify-between bg-gray-50 p-2 rounded">
                  <span className="text-sm">{st.titulo}</span>
                  <Button
                    size="sm"
                    variant="ghost"
                    className="h-6 text-red-600"
                    onClick={() => onEliminarSubtarea(index)}
                  >
                    <Trash2 className="w-3 h-3" />
                  </Button>
                </div>
              ))}
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancelar</Button>
          <Button onClick={onSave} className="bg-emerald-600 hover:bg-emerald-700">
            {editingTarea ? 'Actualizar' : 'Crear Tarea'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
